import styled from '@emotion/styled';
import classNames from 'classnames';

import { Player } from 'modules/players/domain/interfaces/player';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { getBirthDay, getFullName } from 'shared/utils';
import { theme } from 'styles/theme/theme';
import overrideSelectComponents from 'ud-form/components/select/component/override';
import * as S from 'ud-form/components/select/component/styles';
import { UDText } from 'ud-ui/components';
import UDLink from 'ud-ui/components/link';

export const PlayerItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;

  & img {
    object-fit: cover;
    width: 40px;
    height: 40px;
    border-radius: 8px;
  }
`;

export const LoadingMessage = () => {
  return (
    <UDText type="subhead" color={theme.colors.WhiteGray}>
      Загрузка...
    </UDText>
  );
};

const Option = styled(S.Option)`
  transition: background-color 0.25s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.AccentCornsilk};
  }
`;

export const SelectComponent = {
  ...overrideSelectComponents,
  Option: (props: any) => {
    const className = classNames('flex items-center justify-between px-4 w-full', props.isMulti ? 'py-1' : 'py-2');

    return (
      <Option
        {...props.innerProps}
        className={className}
        style={{
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <UDText type="subhead" className="w-full">
          {props.children}
        </UDText>
        {props.isMulti && <S.OptionCheckbox data-checked={props.isSelected} />}
      </Option>
    );
  },
  MenuList: (props: any) => {
    const { cx, ...restProps } = props;
    const { innerProps } = props;
    return (
      <S.MenuList
        {...restProps}
        {...innerProps}
        className="scrollbar-rounded"
        style={{
          maxHeight: '222px',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {props.children}
      </S.MenuList>
    );
  },
  SingleValue: (props: any) => {
    const value = props.data?.value;
    return (
      <div>
        <UDText type="subhead">{getFullName(value)}</UDText>
      </div>
    );
  },
  NoOptionsMessage: () => {
    return (
      <div className="flex-center flex-col p-8">
        <UDText type="subhead" className="text-surfaceRomanSilver30 mb-2">
          Нет совпадений
        </UDText>
      </div>
    );
  },
};

export const Label = (props?: { player?: Player }) => {
  const player = props?.player;
  const fullName = player ? getFullName(player) : undefined;
  const birthDate = player
    ? getBirthDay({
        birthDate: new Date(player.birthDate ?? ''),
        fallback: undefined,
      })
    : undefined;

  return (
    <PlayerItem>
      <img src={player?.photo?.externalUrl ?? USER_DEFAULT_AVATAR} alt={player?.name ?? ''} />
      <UDText type="subhead" color={theme.colors.Black} className="truncate" title={fullName}>
        {fullName}
      </UDText>
      {birthDate && (
        <UDText type="subhead" color={theme.colors.WhiteGray} className="truncate" title={birthDate}>
          {birthDate}
        </UDText>
      )}
    </PlayerItem>
  );
};

export const InfoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

export const InfoImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  object-fit: cover;
`;

export const InfoItem = ({ label, value }: { label: string; value: string | number | undefined }) => {
  return (
    <div className="flex flex-col gap-2" style={{ width: '133px' }}>
      <UDText type="subhead" color={theme.colors.SurfaceRomanSilver30}>
        {label}
      </UDText>
      <UDText type="subhead" color={theme.colors.Black} className="truncate" title={value?.toString()}>
        {value ?? '-'}
      </UDText>
    </div>
  );
};

export const Info = (props?: { player?: Player }) => {
  const player = props?.player;
  return (
    <InfoWrapper>
      <InfoImage src={player?.photo?.externalUrl ?? USER_DEFAULT_AVATAR} alt={player?.name ?? ''} />
      <InfoItem label="Дата рождения" value={player?.birthDate} />
      <InfoItem label="Рост" value={player?.height} />
      <InfoItem label="Вес" value={player?.weight} />
    </InfoWrapper>
  );
};

export const Link = styled(UDLink)`
  text-decoration: none;
  color: ${theme.colors.LightBlue};
`;
